import { Button, FormControl, Grid, TextField } from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { VaccinationCategory } from "../enums/VaccinationCategory";
import dayjs from "dayjs";
const OtherNameField = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();

  return (
    <Controller
      name="otherName"
      control={control}
      defaultValue=""
      rules={{
        required: "EmptyField",
        maxLength: { value: 120, message: "MaxCharacters" },
      }}
      render={({ field }) => (
        <TextField
          {...field}
          size="small"
          label={t("Name")}
          fullWidth
          required
          error={!!errors.otherName}
          helperText={errors.otherName ? t(errors.otherName.message) : ""}
          inputProps={{ maxLength: 120 }}
          data-testid="appointment.finish-meeting.vaccination.other.name-input"
        />
      )}
    />
  );
};

const LotField = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();

  return (
    <Controller
      name="lot"
      control={control}
      defaultValue=""
      rules={{
        maxLength: { value: 15, message: "MaxCharacters" },
      }}
      render={({ field }) => (
        <TextField
          {...field}
          size="small"
          label={t("Lot")}
          fullWidth
          type="text"
          inputProps={{
            inputMode: "numeric",
            "data-testid": "appointment.finish-meeting.vaccination.other.lot-input",
          }}
          error={!!errors.lot}
          helperText={errors.lot ? t(errors.lot.message) : ""}
          onChange={(e) => {
            let value = e.target.value;
            value = value.replace(/\D/g, ""); //Removes everything besides numbers
            if (value.length > 15) {
              value = value.slice(0, 15);
            }
            field.onChange(value);
          }}
        />
      )}
    />
  );
};

const SolventExpirationDateField = () => {
  const { control } = useFormContext();
  const { t } = useTranslation();

  return (
    <Controller
      name="solventExpirationDate"
      control={control}
      render={({ field: { onChange, value } }) => (
        <FormControl fullWidth>
          <MobileDatePicker
            value={value ?? null}
            format="DD/MM/YYYY"
            label={t("SolventExpirationDate")}
            onChange={onChange}
            slotProps={{
              textField: {
                size: "small",
                inputProps: {
                  "data-testid":
                    "appointment.finish-meeting.vaccination.other.solvent-expiration-input",
                },
              },
              previousIconButton: {
                "data-testid":
                  "appointment.finish-meeting.vaccination.other.solvent-expiration-input.prev-month",
              },
              nextIconButton: {
                "data-testid":
                  "appointment.finish-meeting.vaccination.other.solvent-expiration-input.next-month",
              },
              day: (ownerState) => ({
                "data-testid": `appointment.finish-meeting.vaccination.other.solvent-expiration-input.day-${ownerState.day.format("YYYY-MM-DD")}`,
              }),
            }}
            renderInput={(params) => <TextField {...params} />}
          />
        </FormControl>
      )}
    />
  );
};
const PowderExpirationDateField = () => {
  const { control } = useFormContext();
  const { t } = useTranslation();

  return (
    <Controller
      name="powderExpirationDate"
      control={control}
      render={({ field: { onChange, value } }) => (
        <FormControl fullWidth>
          <MobileDatePicker
            value={value ?? null}
            format="DD/MM/YYYY"
            label={t("PowderExpirationDate")}
            onChange={onChange}
            slotProps={{
              textField: {
                size: "small",
                inputProps: {
                  "data-testid":
                    "appointment.finish-meeting.vaccination.other.powder-expiration-input",
                },
              },
              previousIconButton: {
                "data-testid":
                  "appointment.finish-meeting.vaccination.other.powder-expiration-input.prev-month",
              },
              nextIconButton: {
                "data-testid":
                  "appointment.finish-meeting.vaccination.other.powder-expiration-input.next-month",
              },
              day: (ownerState) => ({
                "data-testid": `appointment.finish-meeting.vaccination.other.powder-expiration-input.day-${ownerState.day.format("YYYY-MM-DD")}`,
              }),
            }}
            renderInput={(params) => <TextField {...params} />}
          />
        </FormControl>
      )}
    />
  );
};

export default function AddOtherVaccinationTab({ append }) {
  const { handleSubmit, watch, setValue } = useFormContext();
  const { t } = useTranslation();
  const otherName = watch("otherName");
  const isOtherFormValid = otherName && otherName.trim() !== "";

  const formatDateOrNull = (dateValue) => {
    if (!dateValue) return null;
    if (dayjs.isDayjs(dateValue)) {
      return dateValue.format("DD/MM/YYYY");
    }
    return null;
  };
  function resetValues() {
    const resetStringFields = ["otherName", "lot"];
    resetStringFields.forEach((field) => setValue(field, ""));

    const resetDateFields = ["powderExpirationDate", "solventExpirationDate"];
    resetDateFields.forEach((field) => setValue(field, null));
  }
  const handleAddOther = (data) => {
    const vaccination = {
      vaccinationCategory: VaccinationCategory.OTHER,
      name: data.otherName,
      lot: data.lot ? parseInt(data.lot) : null,
      powderExpirationDate: formatDateOrNull(data.powderExpirationDate),
      solventExpirationDate: formatDateOrNull(data.solventExpirationDate),
      dateOfApplication: formatDateOrNull(data.otherDateOfApplication),
      expirationDate: formatDateOrNull(data.otherExpirationDate),
    };

    append(vaccination);
    resetValues();
  };

  return (
    <form onSubmit={handleSubmit(handleAddOther)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <OtherNameField />
        </Grid>
        <Grid item xs={12}>
          <LotField />
        </Grid>
        <Grid item xs={12}>
          <SolventExpirationDateField />
        </Grid>
        <Grid item xs={12}>
          <PowderExpirationDateField />
        </Grid>

        <Grid item xs={12} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            type="submit"
            sx={{ mb: 2 }}
            disabled={!isOtherFormValid}
            data-testid="appointment.finish-meeting.vaccination.other.add-button"
          >
            {t("Add")}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}
